import { z } from 'zod';

import { reportTypeSchema } from './datacrateModel.schema';
import { demonstrationDisclosureV1Schema } from './demonstration';

/** JSON-safe value accepted at Firestore/GCS contract boundaries. */
export type JsonValue =
  | null
  | boolean
  | number
  | string
  | JsonValue[]
  | { [key: string]: JsonValue };

export const jsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.null(),
    z.boolean(),
    z.number().finite(),
    z.string(),
    z.array(jsonValueSchema),
    z.record(z.string(), jsonValueSchema),
  ]),
);

export const jsonObjectSchema = z.record(z.string(), jsonValueSchema);

const nonEmptyString = z.string().trim().min(1);
const isoDateString = z.string().datetime({ offset: true });
const optionalFinite = z.number().finite().optional();

export const testFitAuthorityRefSchema = z.object({
  scenarioId: nonEmptyString,
  scenarioUpdatedAt: isoDateString,
  inputHash: nonEmptyString,
  resultHash: nonEmptyString,
  modelVersion: nonEmptyString,
  promotedAt: isoDateString,
  layoutPublicationId: nonEmptyString,
  layoutVersion: z.number().int().positive(),
  layoutHash: nonEmptyString,
  layoutPublishedAt: isoDateString,
  sedId: nonEmptyString,
  sedVersion: z.number().int().positive(),
  boundaryHash: nonEmptyString,
}).strict();

/**
 * Historical site defaults can still contain preference fields that moved to
 * `projectIntent` during the SSOT migration. Strip only that closed,
 * explicitly-known compatibility set; the strict schemas below continue to
 * reject every other unknown field.
 */
export const DEPRECATED_PREFERENCE_FIELDS: Readonly<Record<string, readonly string[]>> = {
  feasibility: [
    'projectSizeMW',
    'customerCategory',
    'inServiceDate',
    'riskTolerance',
    'powerSourcePreference',
    'coolingPreference',
    'btmStrategy',
    'firmCapacityBasisMW',
    'datacenterTierTarget',
    'redundancyClass',
    'pueTarget',
    'carbonCommitment',
  ],
  'supply-chain-risk': [
    'projectSizeMW',
    'customerCategory',
    'inServiceDate',
    'riskTolerance',
    'powerSourcePreference',
    'coolingPreference',
    'btmStrategy',
    'firmCapacityBasisMW',
    'datacenterTierTarget',
    'redundancyClass',
    'pueTarget',
    'carbonCommitment',
  ],
  'power-availability': ['powerRequirementMW'],
  // `heatRejectionSink` was briefly persisted by the report-setup form even
  // though it is a display-only projection of projectIntent.bopHeatRejection.
  // Treat it like the earlier `coolingType` SSOT migration so existing sites
  // self-heal at the request boundary without weakening strict validation.
  'water-resource': ['coolingType', 'heatRejectionSink'],
};

export function stripDeprecatedPreferencesForType(
  reportType: string,
  input: unknown,
): unknown {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return input;
  const deprecated = DEPRECATED_PREFERENCE_FIELDS[reportType];
  if (!deprecated) return input;
  const canonical = { ...(input as Record<string, unknown>) };
  for (const field of deprecated) delete canonical[field];
  return canonical;
}

export function stripDeprecatedTypedPreferences(input: unknown): unknown {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return input;
  const canonical = { ...(input as Record<string, unknown>) };
  for (const reportType of Object.keys(canonical)) {
    canonical[reportType] = stripDeprecatedPreferencesForType(reportType, canonical[reportType]);
  }
  return canonical;
}

export const siteContextV2Schema = z.object({
  text: z.string().max(8_000),
  updatedAt: z.string().max(64),
  updatedBy: z.object({ uid: nonEmptyString, email: z.string().email().optional() }).strict().optional(),
  claims: z.array(jsonObjectSchema).max(2_000),
  conflicts: z.array(jsonObjectSchema).max(2_000),
  acknowledgedConflictIds: z.array(z.string().max(256)).max(2_000),
  extractorVersion: z.string().max(256),
  extractorStatus: z.enum([
    'idle',
    'extracting',
    'success',
    'auth-error',
    'quota-error',
    'timeout',
    'parse-error',
    'unknown-error',
  ]).optional(),
  extractorError: z.string().max(2_000).optional(),
  extractedAt: z.string().max(64).optional(),
}).strict();

export const projectIntentV2Schema = z.object({
  workloadProfile: z.enum([
    'ai-training',
    'ai-inference',
    'hpc',
    'enterprise',
    'colocation',
    'edge',
    'mixed',
  ]).optional(),
  projectType: z.enum([
    'ai-datacenter',
    'hpc',
    'edge',
    'colocation',
    'enterprise',
    'undecided',
  ]).optional(),
  powerRequirement: z.enum([
    'under-10mw',
    '10-50mw',
    '50-200mw',
    '200-500mw',
    '500-1000mw',
    '1000-1500mw',
    'over-1500mw',
    'over-200mw',
    'undecided',
  ]),
  powerRequirementMW: z.number().finite().nonnegative().optional(), // SSOT-SENTINEL-OK — canonical projectIntent value, not a per-report preference
  coolingStrategy: z.enum(['air', 'liquid', 'hybrid', 'undecided']),
  nuclearInterest: z.enum(['primary', 'considering', 'no', 'undecided']),
  timeline: z.enum(['under-2yr', '2-5yr', 'over-5yr', 'undecided']),
  inServiceDate: z.string().optional(),
  priority: z.enum(['cost', 'speed', 'sustainability', 'resilience', 'undecided']),
}).catchall(jsonValueSchema);

const feasibilityPreferencesSchema = z.object({
  kpiWeightOverrides: z.partialRecord(
    z.enum(['grid', 'cooling', 'site', 'regulatory', 'logistics']),
    z.number().finite().nonnegative(),
  ).optional(),
}).strict();

const environmentalPreferencesSchema = z.object({
  expectedNepaPathway: z.enum(['catex', 'ea', 'eis', 'unknown']).optional(),
  federalNexus: z.boolean().optional(),
  speciesConcerns: z.array(z.string()).optional(),
  tribalLandProximityKnown: z.boolean().optional(),
  airBasinOverride: z.enum([
    'attainment',
    'non-attainment-marginal',
    'non-attainment-serious',
    'auto',
  ]).optional(),
  dischargeWaterTarget: z.enum(['npdes', 'zld', 'reuse', 'unknown']).optional(),
}).strict();

const testFitPreferencesSchema = z.object({
  targetFootprintAcres: optionalFinite,
  phasing: z.enum(['single', 'phased']).optional(),
  phaseCount: z.number().int().positive().optional(),
  setbackOverrideFt: optionalFinite,
  parkingRatioPerEmployee: optionalFinite,
  stormwaterDetentionGal: optionalFinite,
}).strict();

const powerAvailabilityPreferencesSchema = z.object({
  timeToPowerCriticalDate: z.string().optional(),
  renewablesPreference: z.enum(['required', 'preferred', 'agnostic', 'avoided']).optional(),
  btmStrategy: z.enum(['grid-only', 'btm-only', 'hybrid', 'islanded']).optional(),
  maxSubstationDistanceMiAmber: optionalFinite,
  maxSubstationDistanceMiRed: optionalFinite,
  curtailmentRiskTolerance: z.enum(['low', 'medium', 'high']).optional(),
}).strict();

const waterResourcePreferencesSchema = z.object({
  waterRightsKnown: z.boolean().optional(),
  droughtScenario: z.enum(['1-in-10', '1-in-100', 'both']).optional(),
  wastewaterTarget: z.enum(['npdes', 'zld', 'reuse']).optional(),
}).strict();

const interconnectionPreferencesSchema = z.object({
  latencyTargetsMs: z.object({
    aws: optionalFinite,
    azure: optionalFinite,
    gcp: optionalFinite,
  }).strict().optional(),
  carrierDiversityMin: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
  bandwidthGbps: optionalFinite,
  ixPeeringRequired: z.boolean().optional(),
  submarineCableProximityImportant: z.boolean().optional(),
}).strict();

const publicSentimentPreferencesSchema = z.object({
  trendWindowDays: z.number().int().min(30).max(1825).optional(),
  benchmarkCohortSize: z.number().int().min(3).max(12).optional(),
  includeTribalRecords: z.boolean().optional(),
}).strict();

export const customBriefPreferencesSchema = z.object({
  briefName: nonEmptyString,
  objective: z.string().optional(),
  questions: z.array(z.object({ id: nonEmptyString, text: nonEmptyString }).strict()).min(1),
  contextNotes: z.string().optional(),
  counterparties: z.string().optional(),
  keyDates: z.string().optional(),
}).strict();

const financialPreferencesSchema = z.object({
  hurdleRateOverride: z.number().finite().min(0).max(1).optional(),
  pinnedPathwayComparisonId: nonEmptyString.optional(),
  includeTaxCredits: z.boolean().optional(),
  includePpaAdjustment: z.boolean().optional(),
}).strict();

const executiveSummaryPreferencesSchema = z.object({
  sourceReportIds: z.array(nonEmptyString).min(1),
  synthesisFocus: z.string().optional(),
}).strict();

/** Runtime validator for the complete typed-preference map. */
export const typedPreferencesByTypeV2Schema = z.object({
  feasibility: feasibilityPreferencesSchema.optional(),
  environmental: environmentalPreferencesSchema.optional(),
  'test-fit': testFitPreferencesSchema.optional(),
  'power-availability': powerAvailabilityPreferencesSchema.optional(),
  'water-resource': waterResourcePreferencesSchema.optional(),
  interconnection: interconnectionPreferencesSchema.optional(),
  'supply-chain-risk': feasibilityPreferencesSchema.optional(),
  'public-sentiment': publicSentimentPreferencesSchema.optional(),
  'custom-brief': customBriefPreferencesSchema.optional(),
  financial: financialPreferencesSchema.optional(),
  'executive-summary': executiveSummaryPreferencesSchema.optional(),
}).strict();

const documentReferenceSchema = z.object({
  docId: nonEmptyString,
  relevance: z.number().finite().min(0).max(1),
  userIncluded: z.boolean(),
  reasonIfExcluded: z.string().optional(),
  includedAt: z.string().optional(),
  includedBy: z.string().optional(),
}).strict();

/**
 * Single request contract used by client, API, Firestore and worker boundaries.
 * Complex site snapshots remain JSON objects here; their domain validators run
 * before this envelope is created and the envelope guarantees JSON safety.
 */
export const reportGenerationRequestV2Schema = z.object({
  schemaVersion: z.literal(2),
  siteId: nonEmptyString,
  reportTypeId: reportTypeSchema,
  mode: z.enum(['new', 'update', 'replace', 'add-another']).default('new'),
  parentReportId: nonEmptyString.optional(),
  typedPreferences: typedPreferencesByTypeV2Schema.optional(),
  companyArchetypeOverride: z.enum([
    'developer',
    'hyperscaler',
    'colocation',
    'utility',
    'ipp',
    'energy-major',
    'sovereign',
    'consultancy',
    'financial-sponsor',
    'owner-operator',
    'epc',
    'oem',
    'research',
    'other',
  ]).optional(),
  documentSelection: z.object({
    documentRefs: z.array(documentReferenceSchema),
    documentSelectionMode: z.enum(['auto', 'mixed', 'manual']),
  }).strict().optional(),
  siteContextOverride: siteContextV2Schema.optional(),
  siteConfigOverrides: z.object({
    projectIntentOverride: projectIntentV2Schema.optional(),
    typedPreferencesOverride: typedPreferencesByTypeV2Schema.optional(),
    siteContextOverride: siteContextV2Schema.optional(),
    queuedEvidenceWait: z.boolean().optional(),
    providerCoverageDispatchWaivers: z.array(
      z.object({ providerId: nonEmptyString }).strict(),
    ).max(100).optional(),
    allowCitationDensityOverride: z.boolean().optional(),
    citationDensityRegenerateFocus: z.boolean().optional(),
    citationSupportRegenerateFocus: z.string().max(2_000).optional(),
    generationTier: z.enum(['draft', 'investment-grade']).optional(),
    sectionColumnLayouts: z.record(
      z.string(),
      z.union([z.literal(1), z.literal(2), z.literal(3)]),
    ).optional(),
    expectedTestFitAuthorityRef: testFitAuthorityRefSchema.optional(),
    // Public Sentiment "Refresh sentiment scan" (only `true`; absent means reuse).
    refreshPublicSentimentScan: z.literal(true).optional(),
  }).strict().optional(),
  templateId: nonEmptyString.optional(),
  templateVersion: z.number().int().positive().optional(),
  // Durable requester-scoped idempotency key. The enqueue route binds this to
  // one request hash and one job before any external trigger occurs.
  clientRunId: nonEmptyString,
  citationOverride: z.object({
    token: nonEmptyString,
    reason: nonEmptyString,
  }).strict().optional(),
  evidenceAdapterVersion: nonEmptyString.optional(),
}).strict().superRefine((request, ctx) => {
  if (
    (request.mode === 'update' || request.mode === 'replace')
    && !request.parentReportId
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['parentReportId'],
      message: `${request.mode} requires parentReportId`,
    });
  }
  if (request.siteConfigOverrides?.refreshPublicSentimentScan && request.reportTypeId !== 'public-sentiment') {
    ctx.addIssue({
      code: 'custom',
      path: ['siteConfigOverrides', 'refreshPublicSentimentScan'],
      message: 'refreshPublicSentimentScan applies only to public-sentiment',
    });
  }
  const typedForSelectedType = request.typedPreferences?.[request.reportTypeId];
  if (request.typedPreferences && typedForSelectedType === undefined) {
    ctx.addIssue({
      code: 'custom',
      path: ['typedPreferences', request.reportTypeId],
      message: 'typedPreferences must include the selected report type',
    });
  }
  if ((request.templateId === undefined) !== (request.templateVersion === undefined)) {
    ctx.addIssue({
      code: 'custom',
      path: request.templateId === undefined ? ['templateId'] : ['templateVersion'],
      message: 'templateId and templateVersion must be supplied together',
    });
  }
});

export type ReportGenerationRequestV2 = z.infer<typeof reportGenerationRequestV2Schema>;

export const narrativeCitationSchema = z.object({
  id: nonEmptyString,
  sourceIds: z.array(nonEmptyString).min(1),
  locator: z.string().optional(),
}).strict();

export const narrativeSectionDraftV2Schema = z.object({
  sectionId: nonEmptyString,
  title: nonEmptyString,
  markdown: z.string(),
  citations: z.array(narrativeCitationSchema),
}).strict();

const narrativeDraftBaseSchema = z.object({
  schemaVersion: z.literal('report-narrative-draft-v2'),
  sections: z.array(narrativeSectionDraftV2Schema),
  model: nonEmptyString,
  promptVersion: nonEmptyString,
  generatedAt: isoDateString,
}).strict();

/** The model-authored contract owns prose and citations only. */
export const reportNarrativeDraftV2Schema = z.discriminatedUnion('reportType', [
  narrativeDraftBaseSchema.extend({ reportType: z.literal('feasibility') }),
  narrativeDraftBaseSchema.extend({ reportType: z.literal('environmental') }),
  narrativeDraftBaseSchema.extend({ reportType: z.literal('test-fit') }),
  narrativeDraftBaseSchema.extend({ reportType: z.literal('power-availability') }),
  narrativeDraftBaseSchema.extend({ reportType: z.literal('water-resource') }),
  narrativeDraftBaseSchema.extend({ reportType: z.literal('interconnection') }),
  narrativeDraftBaseSchema.extend({ reportType: z.literal('supply-chain-risk') }),
  narrativeDraftBaseSchema.extend({ reportType: z.literal('public-sentiment') }),
  narrativeDraftBaseSchema.extend({ reportType: z.literal('custom-brief') }),
  narrativeDraftBaseSchema.extend({ reportType: z.literal('financial') }),
  narrativeDraftBaseSchema.extend({ reportType: z.literal('executive-summary') }),
]);

export type ReportNarrativeDraftV2 = z.infer<typeof reportNarrativeDraftV2Schema>;

const evidenceMetadataShape = {
  unit: z.string().optional(),
  sourceIds: z.array(nonEmptyString),
  confidence: z.number().finite().min(0).max(1).optional(),
  retrievedAt: isoDateString.optional(),
};

export function evidenceValueSchema<T extends z.ZodTypeAny>(valueSchema: T) {
  return z.discriminatedUnion('state', [
    z.object({
      state: z.literal('known'),
      value: valueSchema,
      ...evidenceMetadataShape,
    }).strict(),
    z.object({
      state: z.literal('unknown'),
      reason: nonEmptyString,
      ...evidenceMetadataShape,
    }).strict(),
    z.object({
      state: z.literal('not-applicable'),
      reason: nonEmptyString,
      ...evidenceMetadataShape,
    }).strict(),
    z.object({
      state: z.literal('conflict'),
      candidates: z.array(valueSchema).min(2),
      reason: nonEmptyString,
      ...evidenceMetadataShape,
    }).strict(),
  ]);
}

export type EvidenceValue<T extends JsonValue = JsonValue> =
  | {
      state: 'known';
      value: T;
      unit?: string;
      sourceIds: string[];
      confidence?: number;
      retrievedAt?: string;
    }
  | {
      state: 'unknown' | 'not-applicable';
      reason: string;
      unit?: string;
      sourceIds: string[];
      confidence?: number;
      retrievedAt?: string;
    }
  | {
      state: 'conflict';
      candidates: T[];
      reason: string;
      unit?: string;
      sourceIds: string[];
      confidence?: number;
      retrievedAt?: string;
    };

export const evidenceValueJsonSchema = evidenceValueSchema(jsonValueSchema);

const blockBase = {
  id: nonEmptyString,
  sourceIds: z.array(nonEmptyString),
  citationIds: z.array(nonEmptyString),
};

export const reportDocumentBlockV1Schema = z.discriminatedUnion('kind', [
  z.object({
    ...blockBase,
    kind: z.literal('narrative'),
    markdown: z.string(),
  }).strict(),
  z.object({
    ...blockBase,
    kind: z.literal('kpi'),
    items: z.array(z.object({
      id: nonEmptyString,
      label: nonEmptyString,
      value: evidenceValueJsonSchema,
    }).strict()),
  }).strict(),
  z.object({
    ...blockBase,
    kind: z.literal('table'),
    columns: z.array(z.object({ id: nonEmptyString, label: nonEmptyString }).strict()).min(1),
    rows: z.array(z.object({
      id: nonEmptyString,
      cells: z.record(z.string(), evidenceValueJsonSchema),
    }).strict()),
    caption: z.string().optional(),
  }).strict(),
  z.object({
    ...blockBase,
    kind: z.literal('list'),
    ordered: z.boolean(),
    items: z.array(z.object({ id: nonEmptyString, text: z.string() }).strict()),
  }).strict(),
  z.object({
    ...blockBase,
    kind: z.literal('structured-data'),
    label: nonEmptyString,
    value: jsonValueSchema,
  }).strict(),
  z.object({
    ...blockBase,
    kind: z.literal('legal'),
    text: z.string(),
  }).strict(),
]);

export type ReportDocumentBlockV1 = z.infer<typeof reportDocumentBlockV1Schema>;

export const documentSectionV1Schema = z.object({
  id: nonEmptyString,
  sectionId: nonEmptyString,
  title: nonEmptyString,
  group: z.enum(['overview', 'analysis', 'risks-actions', 'evidence']),
  role: z.enum([
    'narrative',
    'kpi',
    'table',
    'chart',
    'map',
    'decision',
    'appendix',
    'legal',
  ]),
  required: z.boolean(),
  presentationPolicy: z.object({
    surfaceEligibility: z.object({
      privateView: z.boolean(),
      publicView: z.boolean(),
      pdf: z.boolean(),
      docx: z.boolean(),
      pptx: z.boolean(),
      csv: z.boolean(),
    }).strict(),
    preferredSpan: z.enum(['flow', 'full-page-width']),
    splittable: z.boolean(),
    keepRules: z.object({
      headingWithFollowingLines: z.number().int().min(3),
      captionWithFigure: z.boolean(),
      repeatTableHeader: z.boolean(),
      keepRowsIntact: z.boolean(),
    }).strict(),
    continuationLabel: nonEmptyString,
    expectedExhibits: z.array(z.enum(['kpi', 'table', 'chart', 'map', 'references'])),
    omissionPolicy: z.enum(['block-if-missing', 'record-explicit-omission']),
  }).strict(),
  blocks: z.array(reportDocumentBlockV1Schema),
}).strict().superRefine((section, ctx) => {
  const expected = section.required ? 'block-if-missing' : 'record-explicit-omission';
  if (section.presentationPolicy.omissionPolicy !== expected) {
    ctx.addIssue({
      code: 'custom',
      path: ['presentationPolicy', 'omissionPolicy'],
      message: `A ${section.required ? 'required' : 'optional'} section must use ${expected}.`,
    });
  }
});

export const documentOmissionV1Schema = z.object({
  sectionId: nonEmptyString,
  reason: z.enum([
    'not-applicable',
    'not-requested',
    'source-unavailable',
    'insufficient-evidence',
    'legacy-unmapped',
    'redacted-for-publication',
  ]),
  detail: nonEmptyString,
  required: z.boolean(),
}).strict();

export const evidenceSourceGradeSchema = z.enum([
  'verified-professional',
  'authoritative-government',
  'reviewed-landgate',
  'regional-screening',
  'inference',
]);

export const evidenceQualificationSchema = z.enum([
  'verified',
  'screening',
  'estimated',
  'unknown',
  'unavailable',
]);

export const topographicDerivationMethodSchema = z.enum([
  'field-topographic-survey',
  'alta-table-a-item-5-contours',
  'lidar-tin-grade',
  'civil-grading-survey',
]);

export const topographicSurveySupportV1Schema = z.object({
  measuredSlopePct: z.number().finite(),
  topographicContours: z.boolean().optional(),
  contourIntervalFt: z.number().finite().nonnegative().optional(),
  contourIntervalM: z.number().finite().nonnegative().optional(),
  surveyPointsSampled: z.number().int().nonnegative().optional(),
  derivationMethod: topographicDerivationMethodSchema.optional(),
  siteId: nonEmptyString.optional(),
}).strict();

export type TopographicSurveySupportV1 = z.infer<typeof topographicSurveySupportV1Schema>;

export const evidenceAttributionV1Schema = z.object({
  statedBy: z.string().optional(),
  statedByRole: z.string().optional(),
  organisation: z.string().optional(),
  statedOn: z.string().optional(),
  appliesToProject: nonEmptyString.optional(),
  projectName: z.string().optional(),
}).strict();

export const citationDemonstrationDisclosureV1Schema = z.union([
  demonstrationDisclosureV1Schema,
  z.boolean(),
  jsonObjectSchema,
]);

export const documentCitationV1Schema = z.object({
  id: nonEmptyString,
  label: nonEmptyString,
  url: z.string().url().optional(),
  retrievedAt: z.string().optional(),
  sourceField: z.string().optional(),
  public: z.boolean(),
  siteId: nonEmptyString.optional(),
  documentId: nonEmptyString.optional(),
  qualification: evidenceQualificationSchema.optional(),
  status: nonEmptyString.optional(),
  state: nonEmptyString.optional(),
  reviewState: nonEmptyString.optional(),
  coverageStatus: nonEmptyString.optional(),
  sourceGrade: evidenceSourceGradeSchema.optional(),
  available: z.boolean().optional(),
  missing: z.boolean().optional(),
  notes: z.string().optional(),
  summary: z.string().optional(),
  description: z.string().optional(),
  excerpt: z.string().optional(),
  excerptPreview: z.string().optional(),
  sourceQuote: z.string().optional(),
  rationale: z.string().optional(),
  evidenceQuotes: z.array(z.string()).optional(),
  attribution: evidenceAttributionV1Schema.optional(),
  demonstrationDisclosure: citationDemonstrationDisclosureV1Schema.optional(),
  topographicSupport: topographicSurveySupportV1Schema.optional(),
}).strict();

export const documentClaimV1Schema = z.object({
  id: nonEmptyString,
  sectionId: nonEmptyString,
  text: nonEmptyString,
  sourceIds: z.array(nonEmptyString),
  citationIds: z.array(nonEmptyString),
}).strict();

export const documentAssetV1Schema = z.object({
  id: nonEmptyString,
  kind: z.enum(['image', 'chart', 'map', 'table-data', 'attachment']),
  label: nonEmptyString,
  path: z.string().optional(),
  // A document revision is immutable only when every referenced asset has an
  // exact byte identity.  Optional hashes let a mutable uploaded-document path
  // masquerade as frozen evidence and also disappear from the layout manifest's
  // asset hash map, so V1 deliberately fails closed here.
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  mediaType: z.string().optional(),
  altText: z.string().optional(),
  public: z.boolean(),
  siteId: nonEmptyString.optional(),
  documentId: nonEmptyString.optional(),
  sourceField: z.string().optional(),
  qualification: evidenceQualificationSchema.optional(),
  status: nonEmptyString.optional(),
  state: nonEmptyString.optional(),
  reviewState: nonEmptyString.optional(),
  coverageStatus: nonEmptyString.optional(),
  sourceGrade: evidenceSourceGradeSchema.optional(),
  available: z.boolean().optional(),
  missing: z.boolean().optional(),
  notes: z.string().optional(),
  summary: z.string().optional(),
  description: z.string().optional(),
  excerpt: z.string().optional(),
  excerptPreview: z.string().optional(),
  sourceQuote: z.string().optional(),
  rationale: z.string().optional(),
  evidenceQuotes: z.array(z.string()).optional(),
  attribution: evidenceAttributionV1Schema.optional(),
  demonstrationDisclosure: citationDemonstrationDisclosureV1Schema.optional(),
  topographicSupport: topographicSurveySupportV1Schema.optional(),
}).strict();

export const documentQualityDecisionV1Schema = z.object({
  state: z.enum(['pass', 'held', 'degraded', 'draft', 'failed']),
  hardGatesPassed: z.boolean(),
  findings: z.array(z.string()),
  reviewerVerdict: z.enum(['pass', 'needs-revision', 'not-run']).optional(),
  validationMethod: z.literal('deterministic-source-screening').optional(),
}).strict();

export const reportDocumentModelV1Schema = z.object({
  schemaVersion: z.literal('report-document-v1'),
  demonstrationDisclosure: demonstrationDisclosureV1Schema.optional(),
  revisionId: nonEmptyString,
  contentHash: z.string().regex(/^[a-f0-9]{64}$/),
  reportId: nonEmptyString,
  reportType: reportTypeSchema,
  siteId: z.string().optional(),
  siteName: nonEmptyString,
  address: z.string(),
  generatedAt: z.string(),
  sourceVersion: z.object({
    major: z.number().int().positive(),
    minor: z.number().int().nonnegative(),
  }).strict(),
  title: nonEmptyString,
  summary: z.string(),
  sections: z.array(documentSectionV1Schema),
  omissions: z.array(documentOmissionV1Schema),
  citations: z.array(documentCitationV1Schema),
  claims: z.array(documentClaimV1Schema),
  evidence: z.record(z.string(), evidenceValueJsonSchema),
  assets: z.array(documentAssetV1Schema),
  qualityDecision: documentQualityDecisionV1Schema,
}).strict().superRefine((document, ctx) => {
  const sectionIds = new Set<string>();
  const blockIds = new Set<string>();
  for (const [sectionIndex, section] of document.sections.entries()) {
    if (sectionIds.has(section.sectionId)) {
      ctx.addIssue({ code: 'custom', path: ['sections', sectionIndex, 'sectionId'], message: 'Section IDs must be unique.' });
    }
    sectionIds.add(section.sectionId);
    for (const [blockIndex, block] of section.blocks.entries()) {
      if (blockIds.has(block.id)) {
        ctx.addIssue({ code: 'custom', path: ['sections', sectionIndex, 'blocks', blockIndex, 'id'], message: 'Block IDs must be globally unique.' });
      }
      blockIds.add(block.id);
    }
  }
  const omissionsBySection = new Map<string, (typeof document.omissions)[number]>();
  for (const [omissionIndex, omission] of document.omissions.entries()) {
    if (omissionsBySection.has(omission.sectionId)) {
      ctx.addIssue({ code: 'custom', path: ['omissions', omissionIndex, 'sectionId'], message: 'A section may have only one omission record.' });
    }
    omissionsBySection.set(omission.sectionId, omission);
  }
  for (const [sectionIndex, section] of document.sections.entries()) {
    const omission = omissionsBySection.get(section.sectionId);
    if (section.blocks.length === 0 && !omission) {
      ctx.addIssue({ code: 'custom', path: ['sections', sectionIndex, 'blocks'], message: 'An empty section requires an explicit omission record.' });
    }
    if (section.blocks.length > 0 && omission) {
      ctx.addIssue({ code: 'custom', path: ['omissions'], message: `Section ${section.sectionId} cannot be both emitted and omitted.` });
    }
    if (omission && omission.required !== section.required) {
      ctx.addIssue({ code: 'custom', path: ['omissions'], message: `Omission required-state disagrees with section ${section.sectionId}.` });
    }
  }
  for (const [omissionIndex, omission] of document.omissions.entries()) {
    if (!sectionIds.has(omission.sectionId)) {
      ctx.addIssue({ code: 'custom', path: ['omissions', omissionIndex, 'sectionId'], message: 'Omission refers to an unknown section.' });
    }
  }
});

export type ReportDocumentModelV1 = z.infer<typeof reportDocumentModelV1Schema>;

export const reportPdfPageSizeSchema = z.enum(['A4', 'Letter']);
export type ReportPdfPageSize = z.infer<typeof reportPdfPageSizeSchema>;

export const frozenReportLogoAssetV1Schema = z.object({
  assetId: nonEmptyString,
  path: nonEmptyString,
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  mediaType: z.enum(['image/png', 'image/svg+xml']),
  altText: nonEmptyString,
  role: z.literal('customer'),
}).strict();

export type FrozenReportLogoAssetV1 = z.infer<typeof frozenReportLogoAssetV1Schema>;

export const frozenReportLogoOmissionV1Schema = z.object({
  code: z.enum([
    'not-selected',
    'unresolvable-source',
    'source-unavailable',
    'unsupported-format',
  ]),
  reason: nonEmptyString,
}).strict();

export type FrozenReportLogoOmissionV1 = z.infer<typeof frozenReportLogoOmissionV1Schema>;

export const presentationSnapshotV1Schema = z.object({
  schemaVersion: z.literal('presentation-snapshot-v1'),
  presentationHash: z.string().regex(/^[a-f0-9]{64}$/),
  templateId: z.string().nullable(),
  templateVersion: z.number().int().positive().nullable(),
  themeId: nonEmptyString,
  pageSize: reportPdfPageSizeSchema,
  marginsMm: z.object({ top: z.number(), right: z.number(), bottom: z.number(), left: z.number() }).strict(),
  density: z.enum(['comfortable', 'balanced', 'compact']),
  cover: z.object({ family: nonEmptyString, boundedStandfirst: z.boolean() }).strict(),
  fonts: z.object({
    display: nonEmptyString,
    body: nonEmptyString,
    ui: nonEmptyString,
  }).strict(),
  branding: z.object({
    houseMarkId: z.literal('datacrate-wordmark-v1'),
    companyName: nonEmptyString,
    siteWordmark: nonEmptyString,
    audienceMode: z.enum(['internal', 'customer']),
    logoAssetId: z.string().nullable(),
    logoAsset: frozenReportLogoAssetV1Schema.nullable(),
    logoOmission: frozenReportLogoOmissionV1Schema.nullable(),
    primaryColor: z.string(),
    accentColor: z.string(),
    confidentiality: z.string().nullable(),
  }).strict(),
  headingOverrides: z.record(z.string(), z.string()),
  sectionOrder: z.array(nonEmptyString),
  sectionColumnLayouts: z.record(
    z.string(),
    z.union([z.literal(1), z.literal(2), z.literal(3)]),
  ),
  rendererVersion: nonEmptyString,
}).strict().superRefine((snapshot, ctx) => {
  if ((snapshot.templateId === null) !== (snapshot.templateVersion === null)) {
    ctx.addIssue({
      code: 'custom',
      path: snapshot.templateId === null ? ['templateId'] : ['templateVersion'],
      message: 'A frozen presentation must identify both the template and its version, or neither.',
    });
  }
  const logo = snapshot.branding.logoAsset;
  if ((snapshot.branding.logoAssetId === null) !== (logo === null)) {
    ctx.addIssue({
      code: 'custom',
      path: ['branding', 'logoAssetId'],
      message: 'The frozen branding logo identity and exact asset reference must be present together.',
    });
  } else if (logo && snapshot.branding.logoAssetId !== logo.assetId) {
    ctx.addIssue({
      code: 'custom',
      path: ['branding', 'logoAssetId'],
      message: 'The frozen branding logo identity must match the exact asset reference.',
    });
  }
  if (snapshot.branding.audienceMode === 'internal') {
    if (logo || snapshot.branding.logoOmission) {
      ctx.addIssue({
        code: 'custom',
        path: ['branding'],
        message: 'Internal reports use the frozen house mark and cannot carry customer-logo state.',
      });
    }
  } else if (Boolean(logo) === Boolean(snapshot.branding.logoOmission)) {
    ctx.addIssue({
      code: 'custom',
      path: ['branding'],
      message: 'Customer reports require either one exact frozen logo asset or one explicit fallback omission.',
    });
  }
});

export type PresentationSnapshotV1 = z.infer<typeof presentationSnapshotV1Schema>;

export const layoutValidationResultV1Schema = z.object({
  status: z.enum(['valid', 'invalid']),
  errors: z.array(z.object({ code: nonEmptyString, message: nonEmptyString, page: z.number().int().positive().optional() }).strict()),
  warnings: z.array(z.object({ code: nonEmptyString, message: nonEmptyString, page: z.number().int().positive().optional() }).strict()),
}).strict();

export const pagedLayoutManifestV1Schema = z.object({
  schemaVersion: z.literal('paged-layout-manifest-v1'),
  reportRevisionId: nonEmptyString,
  layoutFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  pageBreakFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  pageSize: reportPdfPageSizeSchema,
  pages: z.array(z.object({
    index: z.number().int().positive(),
    label: nonEmptyString,
    role: z.enum(['cover', 'front-matter', 'body', 'divider', 'appendix', 'legal']),
    archetype: z.enum([
      'cover',
      'executive-decision',
      'kpi-scorecard',
      'narrative',
      'comparison',
      'table',
      'chart',
      'map-site',
      'risk-action',
      'appendix',
      'legal',
    ]).optional(),
    orientation: z.enum(['portrait', 'landscape']),
    sectionIds: z.array(nonEmptyString),
    utilization: z.number().min(0).max(1),
  }).strict()).min(1),
  sectionPageRanges: z.record(z.string(), z.object({
    start: z.number().int().positive(),
    end: z.number().int().positive(),
  }).strict()),
  bookmarks: z.array(z.object({ title: nonEmptyString, destinationId: nonEmptyString, level: z.number().int().nonnegative() }).strict()),
  namedDestinations: z.record(z.string(), z.object({ page: z.number().int().positive(), topPt: z.number().finite() }).strict()),
  assetHashes: z.record(z.string(), z.string()),
  fontHashes: z.record(z.string(), z.string()),
  validation: layoutValidationResultV1Schema,
}).strict();

export type PagedLayoutManifestV1 = z.infer<typeof pagedLayoutManifestV1Schema>;

export const projectionManifestV1Schema = z.object({
  schemaVersion: z.literal('projection-manifest-v1'),
  reportRevisionId: nonEmptyString,
  surface: z.enum(['private-view', 'public-view', 'pdf', 'docx', 'pptx', 'csv']),
  entries: z.array(z.object({
    /** Stable canonical semantic-item identity. The legacy field name is kept
     * for persisted V1 compatibility; entries also cover fields, omissions,
     * citations, claims, evidence, assets and governance—not only blocks. */
    blockId: nonEmptyString,
    outcome: z.enum(['emitted', 'condensed', 'transformed', 'omitted']),
    targetIds: z.array(nonEmptyString),
    policyReason: z.string().optional(),
    /** Hash of the exact canonical source item, including its stable ID. */
    sourceContentHash: z.string().regex(/^[a-f0-9]{64}$/).optional(),
    /** Hash of each native package/record target discovered in artifact bytes. */
    targetContentHashes: z.record(z.string(), z.string().regex(/^[a-f0-9]{64}$/)).optional(),
    verification: z.object({
      method: z.enum(['semantic-content', 'policy-omission']),
      atomCount: z.number().int().nonnegative(),
      matchedAtomHashes: z.array(z.string().regex(/^[a-f0-9]{64}$/)),
      coverage: z.number().min(0).max(1),
    }).strict().optional(),
  }).strict()),
  complete: z.boolean(),
  generatedAt: isoDateString,
  /** Present on byte-verified delivery projections. Kept optional so legacy
   * PDF/public manifests remain readable until their renderers adopt the same
   * proof contract. */
  accountability: z.object({
    version: z.literal('projection-accountability-v1'),
    artifactSha256: z.string().regex(/^[a-f0-9]{64}$/),
    tracePart: z.object({
      path: nonEmptyString,
      sha256: z.string().regex(/^[a-f0-9]{64}$/),
    }).strict().optional(),
  }).strict().optional(),
}).strict().superRefine((manifest, ctx) => {
  if (!manifest.accountability) return;
  if ((manifest.surface === 'docx' || manifest.surface === 'pptx') && !manifest.accountability.tracePart) {
    ctx.addIssue({
      code: 'custom',
      path: ['accountability', 'tracePart'],
      message: 'OOXML accountability requires an immutable package trace part.',
    });
  }
  for (const [index, entry] of manifest.entries.entries()) {
    if (!entry.sourceContentHash || !entry.targetContentHashes || !entry.verification) {
      ctx.addIssue({
        code: 'custom',
        path: ['entries', index],
        message: 'Accountable projection entries require source, target and verification evidence.',
      });
      continue;
    }
    const targetHashIds = Object.keys(entry.targetContentHashes).sort();
    const targetIds = [...entry.targetIds].sort();
    if (targetHashIds.length !== targetIds.length || targetIds.some((id, targetIndex) => id !== targetHashIds[targetIndex])) {
      ctx.addIssue({
        code: 'custom',
        path: ['entries', index, 'targetContentHashes'],
        message: 'Every native target ID must have exactly one content hash.',
      });
    }
    if (entry.outcome === 'omitted') {
      if (entry.targetIds.length > 0 || !entry.policyReason?.trim() || entry.verification.method !== 'policy-omission') {
        ctx.addIssue({
          code: 'custom',
          path: ['entries', index],
          message: 'Policy omissions require a reason and cannot claim artifact targets.',
        });
      }
    } else if (
      entry.verification.method !== 'semantic-content'
      || (manifest.complete && entry.targetIds.length === 0)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['entries', index],
        message: 'A complete emitted, condensed or transformed block requires verified native targets.',
      });
    }
  }
});

export type ProjectionManifestV1 = z.infer<typeof projectionManifestV1Schema>;

export const reportArtifactPointerV1Schema = z.object({
  format: z.enum(['pdf', 'docx', 'pptx', 'csv']),
  /** Explicit only for alternate PDF geometry. The generation-time A4
   * pointer remains backward-compatible and therefore may omit this field. */
  pageSize: reportPdfPageSizeSchema.optional(),
  path: nonEmptyString,
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  mediaType: nonEmptyString,
  byteLength: z.number().int().nonnegative(),
  state: z.enum(['staged', 'ready', 'failed']),
  layoutStatus: z.enum(['pending', 'valid', 'invalid', 'not-applicable']),
  layoutFingerprint: z.string().regex(/^[a-f0-9]{64}$/).optional(),
  pageBreakFingerprint: z.string().regex(/^[a-f0-9]{64}$/).optional(),
  pageCount: z.number().int().positive().optional(),
  slideCount: z.number().int().positive().optional(),
}).strict().superRefine((artifact, ctx) => {
  if (artifact.pageSize && artifact.format !== 'pdf') {
    ctx.addIssue({
      code: 'custom',
      path: ['pageSize'],
      message: 'Only PDF artifact pointers may carry a page-size variant.',
    });
  }
});

export type ReportArtifactPointerV1 = z.infer<typeof reportArtifactPointerV1Schema>;

/**
 * One immutable PDF geometry variant. The envelope binds the exact bytes to
 * the frozen presentation snapshot and all validation/accountability
 * manifests needed to reproduce and authorize that projection.
 */
export const reportPdfArtifactVariantV1Schema = z.object({
  schemaVersion: z.literal('report-pdf-artifact-variant-v1'),
  pageSize: reportPdfPageSizeSchema,
  presentationPath: nonEmptyString,
  presentationHash: z.string().regex(/^[a-f0-9]{64}$/),
  artifact: reportArtifactPointerV1Schema,
  layoutManifestPath: nonEmptyString,
  projectionManifestPath: nonEmptyString,
  artifactManifestPath: nonEmptyString,
}).strict().superRefine((variant, ctx) => {
  if (variant.artifact.format !== 'pdf') {
    ctx.addIssue({ code: 'custom', path: ['artifact', 'format'], message: 'A PDF variant must contain a PDF artifact.' });
  }
  if (variant.artifact.pageSize !== variant.pageSize) {
    ctx.addIssue({ code: 'custom', path: ['artifact', 'pageSize'], message: 'PDF pointer page size must match its variant envelope.' });
  }
  if (
    variant.artifact.state !== 'ready'
    || variant.artifact.layoutStatus !== 'valid'
    || !variant.artifact.layoutFingerprint
    || !variant.artifact.pageBreakFingerprint
    || !variant.artifact.pageCount
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['artifact'],
      message: 'A persisted PDF variant must be ready, layout-valid, and carry complete page identity.',
    });
  }
});

export type ReportPdfArtifactVariantV1 = z.infer<typeof reportPdfArtifactVariantV1Schema>;

export const artifactManifestV1Schema = z.object({
  schemaVersion: z.literal('artifact-manifest-v1'),
  reportRevisionId: nonEmptyString,
  sourceDocumentHash: z.string().regex(/^[a-f0-9]{64}$/),
  rendererVersion: nonEmptyString,
  /** Present for an explicitly generated PDF geometry variant. */
  presentationHash: z.string().regex(/^[a-f0-9]{64}$/).optional(),
  pdfPageSize: reportPdfPageSizeSchema.optional(),
  qualityState: z.enum(['pass', 'held', 'degraded', 'draft', 'failed']),
  artifacts: z.object({
    pdf: reportArtifactPointerV1Schema.optional(),
    docx: reportArtifactPointerV1Schema.optional(),
    pptx: reportArtifactPointerV1Schema.optional(),
    csv: reportArtifactPointerV1Schema.optional(),
  }).strict(),
  createdAt: isoDateString,
}).strict().superRefine((manifest, ctx) => {
  if (manifest.pdfPageSize) {
    const pdf = manifest.artifacts.pdf;
    if (!pdf || pdf.pageSize !== manifest.pdfPageSize || !manifest.presentationHash) {
      ctx.addIssue({
        code: 'custom',
        path: ['pdfPageSize'],
        message: 'A PDF variant manifest must bind matching PDF bytes and a frozen presentation hash.',
      });
    }
  }
});

export type ArtifactManifestV1 = z.infer<typeof artifactManifestV1Schema>;

export const deliveryBlockReasonSchema = z.object({
  code: z.enum([
    'unauthorized',
    'expired',
    'body-unavailable',
    'metadata-only',
    'schema-invalid',
    'hard-gate-failed',
    'quality-held',
    'degraded',
    'draft',
    'retired-evidence',
    'artifact-unavailable',
    'layout-pending',
    'layout-invalid',
    'projection-incomplete',
    'legacy-validation-required',
  ]),
  message: nonEmptyString,
  overrideable: z.boolean(),
}).strict();

export const deliveryDecisionSchema = z.object({
  schemaVersion: z.literal('delivery-decision-v1'),
  state: z.enum(['ready', 'view-only', 'blocked']),
  canViewPrivate: z.boolean(),
  canExport: z.boolean(),
  canPublish: z.boolean(),
  reasons: z.array(deliveryBlockReasonSchema),
}).strict().superRefine((decision, ctx) => {
  const inconsistent =
    (decision.state === 'ready'
      && (!decision.canViewPrivate || !decision.canExport || !decision.canPublish || decision.reasons.length > 0))
    || (decision.state === 'view-only'
      && (!decision.canViewPrivate || decision.canExport || decision.canPublish || decision.reasons.length === 0))
    || (decision.state === 'blocked'
      && (decision.canViewPrivate || decision.canExport || decision.canPublish || decision.reasons.length === 0));
  if (inconsistent) {
    ctx.addIssue({
      code: 'custom',
      message: 'Delivery state, permissions and reasons are internally inconsistent.',
    });
  }
});

export type DeliveryDecision = z.infer<typeof deliveryDecisionSchema>;

export const reportRevisionReferenceV1Schema = z.object({
  schemaVersion: z.literal('report-revision-ref-v1'),
  revisionId: nonEmptyString,
  /** Immutable post-hygiene SiteRagReport input used to derive the semantic
   * document. Optional only for legacy V1 readers created before this field. */
  sourcePath: nonEmptyString.optional(),
  sourceHash: z.string().regex(/^[a-f0-9]{64}$/).optional(),
  documentPath: nonEmptyString,
  documentHash: z.string().regex(/^[a-f0-9]{64}$/),
  presentationPath: nonEmptyString,
  presentationHash: z.string().regex(/^[a-f0-9]{64}$/),
  artifacts: z.object({
    pdf: reportArtifactPointerV1Schema.optional(),
    docx: reportArtifactPointerV1Schema.optional(),
    pptx: reportArtifactPointerV1Schema.optional(),
    csv: reportArtifactPointerV1Schema.optional(),
  }).strict(),
  /** Generation-time A4 remains `artifacts.pdf`. Explicit alternate page
   * geometries live here so they never replace the default download bytes. */
  pdfVariants: z.object({
    A4: reportPdfArtifactVariantV1Schema.optional(),
    Letter: reportPdfArtifactVariantV1Schema.optional(),
  }).strict().optional(),
  deliveryDecision: deliveryDecisionSchema,
}).strict().superRefine((revision, ctx) => {
  if (Boolean(revision.sourcePath) !== Boolean(revision.sourceHash)) {
    ctx.addIssue({
      code: 'custom',
      path: ['sourcePath'],
      message: 'Immutable source path and hash must be supplied together.',
    });
  }
  for (const [key, artifact] of Object.entries(revision.artifacts)) {
    if (artifact && artifact.format !== key) {
      ctx.addIssue({
        code: 'custom',
        path: ['artifacts', key, 'format'],
        message: `Artifact key ${key} must contain a ${key} pointer.`,
      });
    }
  }
  for (const [pageSize, variant] of Object.entries(revision.pdfVariants ?? {})) {
    if (variant && variant.pageSize !== pageSize) {
      ctx.addIssue({
        code: 'custom',
        path: ['pdfVariants', pageSize, 'pageSize'],
        message: `PDF variant key ${pageSize} must contain matching page geometry.`,
      });
    }
  }
});

export type ReportRevisionReferenceV1 = z.infer<typeof reportRevisionReferenceV1Schema>;

const PUBLIC_REPORT_SOURCE_ID_PATTERN = /^(?:ref|provider|provenance|citation):/i;
const PUBLIC_STRUCTURED_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 _.:\-]{0,127}$/;
const PRIVATE_STRUCTURED_KEYS = new Set([
  'accesstoken',
  'bucketpath',
  'contributingdocids',
  'docid',
  'docids',
  'documentid',
  'documentids',
  'downloadtoken',
  'gcspath',
  'gcsstoragepath',
  'objectpath',
  'privatesourceid',
  'privatesourceids',
  'rawfilepath',
  'rawstoragepath',
  'signedurl',
  'sourcedocid',
  'sourcedocids',
  'sourcedocumentid',
  'sourcedocumentids',
  'sourcehandle',
  'sourcehandles',
  'storagepath',
]);

const normalizeStructuredKey = (key: string): string => key.replace(/[^A-Za-z0-9]/g, '').toLowerCase();

/** Source identities which are explicitly safe to disclose in public lineage. */
export function isPublicReportSourceIdV1(value: string): boolean {
  return PUBLIC_REPORT_SOURCE_ID_PATTERN.test(value)
    && !isPrivatePublicProjectionStringV1(value);
}

/**
 * Structured public data is an allowlisted JSON surface. Keys must use a
 * bounded, printable identifier form and cannot name private storage,
 * uploaded-document or source-handle fields at any nesting depth.
 */
export function isAllowedPublicStructuredKeyV1(key: string): boolean {
  return PUBLIC_STRUCTURED_KEY_PATTERN.test(key)
    && !PRIVATE_STRUCTURED_KEYS.has(normalizeStructuredKey(key))
    && key !== '__proto__'
    && key !== 'prototype'
    && key !== 'constructor';
}

const PRIVATE_PUBLIC_TEXT_PATTERNS = [
  /\b(?:doc(?:ument)?|upload|attachment):[A-Za-z0-9_.\/-]+/gi,
  /\b(?:gs|gcs|firestore):\/\/[^\s<>"'`\])},;]+/gi,
  /\busers\/[^/\s]+\/(?:documents|reports|uploads)\/[^\s<>"'`\])},;]+/gi,
  /\bprivate-reports\/[^\s<>"'`\])},;]+/gi,
  /https?:\/\/(?:localhost|127\.0\.0\.1|[^/\s]+\.internal)(?:\/[^\s<>"'`]*)?/gi,
  /https?:\/\/[^\s<>"'`]*(?:[?&](?:token|access_token)=|X-Goog-(?:Signature|Credential)=|GoogleAccessId=)[^\s<>"'`]*/gi,
] as const;

/** Replace private capabilities/handles wherever they occur in public prose or data. */
export function redactPrivatePublicProjectionTextV1(value: string): string {
  return PRIVATE_PUBLIC_TEXT_PATTERNS.reduce(
    (text, pattern) => text.replace(pattern, '[private source withheld]'),
    value,
  );
}

/** Values which contain a private capability/handle, including embedded prose. */
export function isPrivatePublicProjectionStringV1(value: string): boolean {
  return redactPrivatePublicProjectionTextV1(value) !== value;
}

interface PublicStructuredValueIssue {
  path: Array<string | number>;
  message: string;
}

/** Deep validation twin of the public sanitizer; used at the persisted schema boundary. */
export function findPublicStructuredValueIssuesV1(
  value: JsonValue,
  path: Array<string | number> = [],
): PublicStructuredValueIssue[] {
  const issues: PublicStructuredValueIssue[] = [];
  const visit = (current: JsonValue, currentPath: Array<string | number>): void => {
    if (typeof current === 'string') {
      if (isPrivatePublicProjectionStringV1(current)) {
        issues.push({ path: currentPath, message: 'Private source or storage handles are not public data.' });
      }
      return;
    }
    if (current === null || typeof current !== 'object') return;
    if (Array.isArray(current)) {
      current.forEach((entry, index) => visit(entry, [...currentPath, index]));
      return;
    }
    for (const [key, entry] of Object.entries(current)) {
      if (!isAllowedPublicStructuredKeyV1(key)) {
        issues.push({ path: [...currentPath, key], message: 'This structured-data key is not allowed in a public report.' });
        continue;
      }
      visit(entry, [...currentPath, key]);
    }
  };
  visit(value, path);
  return issues;
}

export const publicReportSnapshotV2Schema = z.object({
  schemaVersion: z.literal('public-report-snapshot-v2'),
  reportId: nonEmptyString,
  /** Exact private immutable revision from which this sanitized public
   * projection was derived. This is deliberately separate from
   * `document.revisionId`, which identifies the redacted public document. */
  sourceReportRevisionId: nonEmptyString,
  reportType: reportTypeSchema,
  sharedBy: z.string().email().nullable(),
  sharedAt: isoDateString,
  document: reportDocumentModelV1Schema,
  presentation: presentationSnapshotV1Schema,
  redactionManifest: projectionManifestV1Schema,
  pdfProjectionManifest: projectionManifestV1Schema.optional(),
  artifactManifest: artifactManifestV1Schema.optional(),
  qualityDecision: documentQualityDecisionV1Schema,
  deliveryDecision: deliveryDecisionSchema,
  publicPdfArtifact: reportArtifactPointerV1Schema.nullable(),
}).strict().superRefine((snapshot, ctx) => {
  if (
    snapshot.deliveryDecision.canPublish
    && (
      !snapshot.publicPdfArtifact
      || snapshot.publicPdfArtifact.format !== 'pdf'
      || snapshot.publicPdfArtifact.state !== 'ready'
      || snapshot.publicPdfArtifact.layoutStatus !== 'valid'
      || !snapshot.pdfProjectionManifest
      || snapshot.pdfProjectionManifest.surface !== 'pdf'
      || !snapshot.pdfProjectionManifest.complete
      || snapshot.pdfProjectionManifest.accountability?.artifactSha256 !== snapshot.publicPdfArtifact.sha256
      || !snapshot.artifactManifest
      || snapshot.artifactManifest.reportRevisionId !== snapshot.document.revisionId
      || snapshot.artifactManifest.sourceDocumentHash !== snapshot.document.contentHash
      || snapshot.artifactManifest.artifacts.pdf?.sha256 !== snapshot.publicPdfArtifact.sha256
    )
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['publicPdfArtifact'],
      message: 'A publishable snapshot requires a byte-accountable, layout-valid public PDF and artifact manifest.',
    });
  }

  const addSourceIdIssues = (sourceIds: readonly string[], path: Array<string | number>): void => {
    sourceIds.forEach((sourceId, index) => {
      if (!isPublicReportSourceIdV1(sourceId)) {
        ctx.addIssue({
          code: 'custom',
          path: [...path, index],
          message: 'A public report cannot contain a private source identity.',
        });
      }
    });
  };
  const addStructuredIssues = (value: JsonValue, path: Array<string | number>): void => {
    for (const issue of findPublicStructuredValueIssuesV1(value, path)) {
      ctx.addIssue({ code: 'custom', path: issue.path, message: issue.message });
    }
  };
  const addPublicTextIssue = (value: string | undefined, path: Array<string | number>): void => {
    if (value && isPrivatePublicProjectionStringV1(value)) {
      ctx.addIssue({
        code: 'custom',
        path,
        message: 'Public report prose cannot contain a private source or storage handle.',
      });
    }
  };
  const addEvidenceIssues = (
    evidence: z.infer<typeof evidenceValueJsonSchema>,
    path: Array<string | number>,
  ): void => {
    addSourceIdIssues(evidence.sourceIds, [...path, 'sourceIds']);
    addPublicTextIssue(evidence.unit, [...path, 'unit']);
    if (evidence.state !== 'known') addPublicTextIssue(evidence.reason, [...path, 'reason']);
    if (evidence.state === 'known') addStructuredIssues(evidence.value, [...path, 'value']);
    if (evidence.state === 'conflict') {
      evidence.candidates.forEach((candidate, index) => (
        addStructuredIssues(candidate, [...path, 'candidates', index])
      ));
    }
  };

  addPublicTextIssue(snapshot.document.siteName, ['document', 'siteName']);
  addPublicTextIssue(snapshot.document.address, ['document', 'address']);
  addPublicTextIssue(snapshot.document.title, ['document', 'title']);
  addPublicTextIssue(snapshot.document.summary, ['document', 'summary']);

  snapshot.document.sections.forEach((section, sectionIndex) => {
    addPublicTextIssue(section.id, ['document', 'sections', sectionIndex, 'id']);
    addPublicTextIssue(section.sectionId, ['document', 'sections', sectionIndex, 'sectionId']);
    addPublicTextIssue(section.title, ['document', 'sections', sectionIndex, 'title']);
    addPublicTextIssue(
      section.presentationPolicy.continuationLabel,
      ['document', 'sections', sectionIndex, 'presentationPolicy', 'continuationLabel'],
    );
    section.blocks.forEach((block, blockIndex) => {
      const blockPath = ['document', 'sections', sectionIndex, 'blocks', blockIndex] as Array<string | number>;
      addPublicTextIssue(block.id, [...blockPath, 'id']);
      addSourceIdIssues(block.sourceIds, [...blockPath, 'sourceIds']);
      block.citationIds.forEach((citationId, citationIndex) => {
        addPublicTextIssue(citationId, [...blockPath, 'citationIds', citationIndex]);
      });
      if (block.kind === 'narrative') addPublicTextIssue(block.markdown, [...blockPath, 'markdown']);
      if (block.kind === 'list') {
        block.items.forEach((item, itemIndex) => {
          addPublicTextIssue(item.id, [...blockPath, 'items', itemIndex, 'id']);
          addPublicTextIssue(item.text, [...blockPath, 'items', itemIndex, 'text']);
        });
      }
      if (block.kind === 'legal') addPublicTextIssue(block.text, [...blockPath, 'text']);
      if (block.kind === 'structured-data') {
        addPublicTextIssue(block.label, [...blockPath, 'label']);
        addStructuredIssues(block.value, [...blockPath, 'value']);
      }
      if (block.kind === 'kpi') {
        block.items.forEach((item, itemIndex) => {
          addPublicTextIssue(item.id, [...blockPath, 'items', itemIndex, 'id']);
          addPublicTextIssue(item.label, [...blockPath, 'items', itemIndex, 'label']);
          addEvidenceIssues(item.value, [...blockPath, 'items', itemIndex, 'value']);
        });
      }
      if (block.kind === 'table') {
        block.columns.forEach((column, columnIndex) => {
          addPublicTextIssue(column.id, [...blockPath, 'columns', columnIndex, 'id']);
          addPublicTextIssue(column.label, [...blockPath, 'columns', columnIndex, 'label']);
        });
        addPublicTextIssue(block.caption, [...blockPath, 'caption']);
        block.rows.forEach((row, rowIndex) => {
          addPublicTextIssue(row.id, [...blockPath, 'rows', rowIndex, 'id']);
          for (const [cellId, value] of Object.entries(row.cells)) {
            addPublicTextIssue(cellId, [...blockPath, 'rows', rowIndex, 'cells', cellId]);
            addEvidenceIssues(value, [...blockPath, 'rows', rowIndex, 'cells', cellId]);
          }
        });
      }
    });
  });
  snapshot.document.claims.forEach((claim, claimIndex) => {
    addPublicTextIssue(claim.id, ['document', 'claims', claimIndex, 'id']);
    addPublicTextIssue(claim.text, ['document', 'claims', claimIndex, 'text']);
    addSourceIdIssues(claim.sourceIds, ['document', 'claims', claimIndex, 'sourceIds']);
    claim.citationIds.forEach((citationId, citationIndex) => {
      addPublicTextIssue(citationId, ['document', 'claims', claimIndex, 'citationIds', citationIndex]);
    });
  });
  snapshot.document.omissions.forEach((omission, omissionIndex) => {
    addPublicTextIssue(omission.detail, ['document', 'omissions', omissionIndex, 'detail']);
  });
  snapshot.document.qualityDecision.findings.forEach((finding, findingIndex) => {
    addPublicTextIssue(finding, ['document', 'qualityDecision', 'findings', findingIndex]);
  });
  for (const [evidenceId, evidence] of Object.entries(snapshot.document.evidence)) {
    if (!isAllowedPublicStructuredKeyV1(evidenceId) || isPrivatePublicProjectionStringV1(evidenceId)) {
      ctx.addIssue({
        code: 'custom',
        path: ['document', 'evidence', evidenceId],
        message: 'A public evidence key cannot be a private source handle.',
      });
      continue;
    }
    addEvidenceIssues(evidence, ['document', 'evidence', evidenceId]);
  }
  snapshot.document.citations.forEach((citation, citationIndex) => {
    addPublicTextIssue(citation.id, ['document', 'citations', citationIndex, 'id']);
    addPublicTextIssue(citation.label, ['document', 'citations', citationIndex, 'label']);
    addPublicTextIssue(citation.sourceField, ['document', 'citations', citationIndex, 'sourceField']);
    if (
      (citation.public && !citation.url)
      || (!citation.public && citation.url !== undefined)
      || (citation.url && isPrivatePublicProjectionStringV1(citation.url))
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['document', 'citations', citationIndex],
        message: 'Public citation metadata is not safe or internally consistent.',
      });
    }
  });
  if (snapshot.document.assets.length > 0) {
    ctx.addIssue({
      code: 'custom',
      path: ['document', 'assets'],
      message: 'Public V2 currently requires separately sanitized assets; private asset references are prohibited.',
    });
  }
  if (
    snapshot.presentation.branding.logoAssetId !== null
    || snapshot.presentation.branding.logoAsset !== null
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['presentation', 'branding', 'logoAsset'],
      message: 'A private branding asset identity or locator cannot be stored in a public snapshot.',
    });
  }
  addPublicTextIssue(snapshot.presentation.themeId, ['presentation', 'themeId']);
  addPublicTextIssue(snapshot.presentation.branding.companyName, [
    'presentation',
    'branding',
    'companyName',
  ]);
  addPublicTextIssue(snapshot.presentation.branding.siteWordmark, [
    'presentation',
    'branding',
    'siteWordmark',
  ]);
  addPublicTextIssue(snapshot.presentation.branding.logoOmission?.reason, [
    'presentation',
    'branding',
    'logoOmission',
    'reason',
  ]);
  addPublicTextIssue(snapshot.presentation.branding.confidentiality ?? undefined, [
    'presentation',
    'branding',
    'confidentiality',
  ]);
  for (const [sectionId, heading] of Object.entries(snapshot.presentation.headingOverrides)) {
    addPublicTextIssue(sectionId, ['presentation', 'headingOverrides', sectionId]);
    addPublicTextIssue(heading, ['presentation', 'headingOverrides', sectionId]);
  }
});

export type PublicReportSnapshotV2 = z.infer<typeof publicReportSnapshotV2Schema>;

const loadFailureBase = z.object({
  reportId: nonEmptyString,
  reason: nonEmptyString,
});

export const reportArtifactLoadResultSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('ready'),
    source: z.enum(['v2', 'legacy']),
    reportId: nonEmptyString,
    document: reportDocumentModelV1Schema.optional(),
    presentation: presentationSnapshotV1Schema.optional(),
    artifacts: artifactManifestV1Schema.optional(),
    deliveryDecision: deliveryDecisionSchema,
  }).strict(),
  loadFailureBase.extend({ status: z.literal('body-unavailable') }).strict(),
  loadFailureBase.extend({ status: z.literal('unauthorized') }).strict(),
  loadFailureBase.extend({ status: z.literal('expired') }).strict(),
  loadFailureBase.extend({ status: z.literal('legacy'), regenerateRequired: z.boolean() }).strict(),
  loadFailureBase.extend({ status: z.literal('failed'), errorCode: nonEmptyString }).strict(),
]).superRefine((result, ctx) => {
  if (
    result.status === 'ready'
    && result.source === 'v2'
    && (!result.document || !result.presentation || !result.artifacts)
  ) {
    ctx.addIssue({
      code: 'custom',
      message: 'A ready V2 load result requires document, presentation and artifact manifests.',
    });
  }
});

export type ReportArtifactLoadResult = z.infer<typeof reportArtifactLoadResultSchema>;
